'use client';

import { useEffect, useState, useRef } from 'react';

import { OnlineChatProps } from './types';

import IconCall from '@/assets/icons/iconCall.svg';
import IconChat from '@/assets/icons/iconChat.svg';
import IconClose from '@/assets/icons/iconClose.svg';
import IconContactUs from '@/assets/icons/iconContactUs.svg';
import IconTelegram from '@/assets/icons/iconTelegramCircle.svg';
import IconWhatsApp from '@/assets/icons/iconWhatsapp.svg';
import { useOnClickOutside } from '@/shared/hooks/useOnClickOutside';
import { useWindowWidth } from '@/shared/hooks/useWindowWidth';
import { Locale } from '@/shared/types';
import getOnlineChatLink from '@/shared/utils/getOnlineChatLink';

// TODO: Move to BE
const translations = {
  ru: {
    contactUsChat: 'Написать в чат',
    contactUsWhatsApp: 'Написать в WhatsApp',
    contactUsWhatsAppLink: 'https://wa.me/79665000050',
    contactUsTelegram: 'Написать в Telegram',
    contactUsTelegramLink: 'https://t.me/geodata2gis',
    contactUsCall: 'Бесплатный звонок по РФ',
    contactUsCallLink: 'tel:88002003600',
  },
  am: {
    contactUsChat: 'Գրել զրուցարանում',
    contactUsWhatsApp: 'Գրել WhatsApp-ով',
    contactUsWhatsAppLink: 'https://wa.me/79665000050',
    contactUsTelegram: 'Գրել Telegram-ով',
    contactUsTelegramLink: 'https://t.me/geodata2gis',
    contactUsCall: 'Անվճար զանգ Ռուսաստանի տարածքում',
    contactUsCallLink: 'tel:88002003600',
  },
  en: {
    contactUsChat: 'Chat with us',
    contactUsWhatsApp: 'Message on WhatsApp',
    contactUsWhatsAppLink: 'https://wa.me/79665000050',
    contactUsTelegram: 'Message on Telegram',
    contactUsTelegramLink: 'https://t.me/geodata2gis',
    contactUsCall: 'Free call within Russia',
    contactUsCallLink: 'tel:88002003600',
  },
  kz: {
    contactUsChat: 'Чатқа жазу',
    contactUsWhatsApp: 'WhatsApp арқылы жазу',
    contactUsWhatsAppLink: 'https://wa.me/79665000050',
    contactUsTelegram: 'Telegram арқылы жазу',
    contactUsTelegramLink: 'https://t.me/geodata2gis',
    contactUsCall: 'Ресей бойынша тегін қоңырау',
    contactUsCallLink: 'tel:88002003600',
  },
  ge: {
    contactUsChat: 'ჩატში დაწერა',
    contactUsWhatsApp: 'დაწერა WhatsApp-ით',
    contactUsWhatsAppLink: 'https://wa.me/79665000050',
    contactUsTelegram: 'დაწერა Telegram-ით',
    contactUsTelegramLink: 'https://t.me/geodata2gis',
    contactUsCall: 'უფასო ზარი რუსეთის ფარგლებში',
    contactUsCallLink: 'tel:88002003600',
  },
};

export const OnlineChat = ({ locale }: OnlineChatProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [isRealUser, setIsRealUser] = useState(false);
  const [isAgentOnline, setIsAgentOnline] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  useOnClickOutside(menuRef, () => setIsMenuOpen(false));
  const width = useWindowWidth();

  useEffect(() => {
    const handleMouseMove = () => {
      setIsRealUser(true);
      document.removeEventListener('pointermove', handleMouseMove);
    };
    document.addEventListener('pointermove', handleMouseMove);

    return () => document.removeEventListener('pointermove', handleMouseMove);
  }, []);

  useEffect(() => {
    const [_, page] = window.location.pathname
      .replace(new RegExp(`^/${locale}(/|$)`), '')
      .split('/');

    if (isRealUser) {
      const script = document.createElement('script');

      // @ts-ignore
      window.jivo_onLoadCallback = () => {
        // @ts-ignore
        if (window.jivo_api.chatMode() === 'offline') {
          const removeJivoButtonStyle = document.createElement('style');
          const removeJivoMobileButtonStyle = document.createElement('style');

          removeJivoButtonStyle.textContent = `
            [class*="__jivoDesktopButton"] [class*="button__"] {
              display: none !important;
            };
          `;
          removeJivoMobileButtonStyle.textContent = `
            .__jivoMobileButton {
              display: none !important;
            };
          `;
          removeJivoButtonStyle.id = 'removeJivoButtonStyle';
          removeJivoMobileButtonStyle.id = 'removeJivoMobileButtonStyle';

          document.head.appendChild(removeJivoButtonStyle);
          document.head.appendChild(removeJivoMobileButtonStyle);

          setIsAgentOnline(false);
          // @ts-ignore
          window.jivo_api.close();
        } else {
          if (width! >= 1024) {
            // @ts-ignore
            setTimeout(() => window.jivo_api.open(), 60000);
          }
        }
      };

      script.src = getOnlineChatLink(locale as Locale, page);
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      //@ts-ignore
      window.jivo_destroy?.();
      //@ts-ignore
      window.jivo_config = undefined;

      const jivoScript = document.querySelector('script[src*="jivo"]');
      const jivoFrame = document.querySelector('iframe[id*="jivo"]');
      const jivoLink = document.querySelector('link[href*="jivo"]');

      const removeJivoButtonStyle = document.querySelector(
        '#removeJivoButtonStyle',
      );
      const removeJivoMobileButtonStyle = document.querySelector(
        '#removeJivoMobileButtonStyle',
      );

      jivoScript?.remove();
      jivoFrame?.remove();
      jivoLink?.remove();
      removeJivoButtonStyle?.remove();
      removeJivoMobileButtonStyle?.remove();
    };
  }, [isRealUser]);

  return (
    <div className="fixed right-5 bottom-5 z-[999]">
      {!isAgentOnline && (
        <button
          className="flex h-[65px] w-[65px] cursor-pointer items-center justify-center rounded-full bg-[#12951aa3] transition-transform hover:scale-110"
          onClick={() => setIsMenuOpen((isMenuOpen) => !isMenuOpen)}
        >
          {isMenuOpen ? <IconClose className="h-8 w-8" /> : <IconContactUs />}
        </button>
      )}
      {isMenuOpen && (
        <div ref={menuRef} className="absolute top-[-195px] right-0">
          <ul className="bg-surface-base-tertiary *:hover:text-status-accent flex w-max flex-col gap-4 rounded-2xl p-5 shadow-[0_20px_40px_8px_rgba(0,0,0,0.25)] *:*:flex *:flex *:cursor-pointer *:*:gap-2 *:gap-2">
            <li
              onClick={() => {
                // @ts-ignore
                window.jivo_api.open();
              }}
            >
              <IconChat className="h-6 w-6 opacity-[0.64]" />
              {translations[locale].contactUsChat}
            </li>
            <li>
              <a
                target="_blank"
                href={translations[locale].contactUsWhatsAppLink}
              >
                <IconWhatsApp className="h-6 w-6 opacity-[0.64]" />
                {translations[locale].contactUsWhatsApp}
              </a>
            </li>
            <li>
              <a
                target="_blank"
                href={translations[locale].contactUsTelegramLink}
              >
                <IconTelegram className="h-6 w-6 opacity-[0.64]" />
                {translations[locale].contactUsTelegram}
              </a>
            </li>
            <li>
              <a target="_blank" href={translations[locale].contactUsCallLink}>
                <IconCall className="h-6 w-6 opacity-[0.64]" />
                {translations[locale].contactUsCall}
              </a>
            </li>
          </ul>
          <div className="border-t-surface-base-tertiary absolute right-3 -bottom-2 h-0 w-0 -translate-x-1/2 border-t-[8px] border-r-[10px] border-l-[10px] border-r-transparent border-l-transparent"></div>
        </div>
      )}
    </div>
  );
};
